import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Api, type DismissalItem } from "./api.js";

/** Per-profile dismissed ids plus an optimistic mutation. Every Home source
 * reads the same query key, so dismissing a duplicate in one row removes it
 * everywhere immediately; the server makes the exclusion durable. */
export function useDismissals(profileId: string | null): {
  dismissedIds: Set<string>;
  dismiss: (item: DismissalItem) => void;
  dismissing: boolean;
} {
  const qc = useQueryClient();
  const key = ["dismissals", profileId] as const;
  // A click is an explicit remove command. Keep that local removal even when
  // the persistence request fails or an old server has no dismissal route;
  // rolling it back makes the X look broken and brings the card straight back.
  const [localIds, setLocalIds] = useState<Set<string>>(new Set());
  useEffect(() => setLocalIds(new Set()), [profileId]);
  const q = useQuery({
    queryKey: key,
    queryFn: () => Api.dismissals.list(profileId!),
    enabled: !!profileId,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (item: DismissalItem) => Api.dismissals.dismiss(profileId!, item),
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<{ itemIds: string[] }>(key);
      qc.setQueryData<{ itemIds: string[] }>(key, (old) => ({
        itemIds: [...new Set([...(old?.itemIds ?? []), item.id])],
      }));
      return { previous };
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      // Re-rank and backfill rows using the new negative preference signal.
      void qc.invalidateQueries({ queryKey: ["recs", profileId] });
    },
  });

  const dismissedIds = useMemo(
    () => new Set([...(q.data?.itemIds ?? []), ...localIds]),
    [q.data, localIds],
  );
  const dismiss = useCallback((item: DismissalItem) => {
    if (!profileId) return;
    setLocalIds((old) => new Set(old).add(item.id));
    mutation.mutate(item);
  }, [profileId, mutation.mutate]);

  return { dismissedIds, dismiss, dismissing: mutation.isPending };
}
